/**
* @license Apache-2.0
*
* Copyright (c) 2026 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

/* eslint-disable no-restricted-syntax, no-invalid-this, max-lines */

'use strict';

// MODULES //

import hasIteratorSymbolSupport from '@stdlib/assert-has-iterator-symbol-support';
import { isPrimitive as isNonNegativeInteger } from '@stdlib/assert-is-nonnegative-integer';
import { primitives as isStringArray } from '@stdlib/assert-is-string-array';
import isCollection from '@stdlib/assert-is-collection';
import isArrayBuffer from '@stdlib/assert-is-arraybuffer';
import isFunction from '@stdlib/assert-is-function';
import { isPrimitive as isInteger } from '@stdlib/assert-is-integer';
import { isPrimitive as isNumber } from '@stdlib/assert-is-number';
import { isPrimitive as isString } from '@stdlib/assert-is-string';
import isObject from '@stdlib/assert-is-object';
import isnan from '@stdlib/math-base-assert-is-nan';
import ITERATOR_SYMBOL from '@stdlib/symbol-iterator';
import setReadOnlyAccessor from '@stdlib/utils-define-nonenumerable-read-only-accessor';
import setReadOnly from '@stdlib/utils-define-nonenumerable-read-only-property';
import setReadWriteAccessor from '@stdlib/utils-define-read-write-accessor';
import floor from '@stdlib/math-base-special-floor';
import format from '@stdlib/error-tools-fmtprodmsg';
import Uint16Array from '@stdlib/array-uint16';
import f16 from '@stdlib/number-float64-base-to-float16';
import toWord from '@stdlib/number-float16-base-to-word';
import fromWord from '@stdlib/number-float16-base-from-word';
import fromArray from './from_array.js';
import fromIterator from './from_iterator.js';
import fromIteratorMap from './from_iterator_map.js';


// VARIABLES //

var BYTES_PER_ELEMENT = Uint16Array.BYTES_PER_ELEMENT;
var HAS_ITERATOR_SYMBOL = hasIteratorSymbolSupport();


// FUNCTIONS //

/**
* Returns a boolean indicating if a value is a `Float16Array`.
*
* @private
* @param {*} value - value to test
* @returns {boolean} boolean indicating if a value is a `Float16Array`
*/
function isFloat16Array( value ) {
	return (
		typeof value === 'object' &&
		value !== null &&
		value.constructor.name === 'Float16Array' &&
		value.BYTES_PER_ELEMENT === BYTES_PER_ELEMENT
	);
}

/**
* Returns a boolean indicating if a value is a floating-point typed array constructor.
*
* @private
* @param {*} value - value to test
* @returns {boolean} boolean indicating if a value is a floating-point typed array constructor
*/
function isFloatingPointArrayConstructor( value ) { // eslint-disable-line id-length
	return ( value === Float16Array );
}

/**
* Returns a getter.
*
* @private
* @param {Float16Array} ctx - typed array instance
* @param {NonNegativeInteger} idx - element index
* @returns {Function} getter
*/
function getter( ctx, idx ) {
	return get;

	/**
	* Returns an array element.
	*
	* @private
	* @returns {(number|void)} array element
	*/
	function get() {
		return ctx._get( idx ); // eslint-disable-line no-underscore-dangle
	}
}

/**
* Returns a setter.
*
* @private
* @param {Float16Array} ctx - typed array instance
* @param {NonNegativeInteger} idx - element index
* @returns {Function} setter
*/
function setter( ctx, idx ) {
	return set;

	/**
	* Sets an array element.
	*
	* @private
	* @param {number} value - value to set
	*/
	function set( value ) {
		ctx.set( [ value ], idx );
	}
}


// MAIN //

/**
* 16-bit floating-point number array constructor.
*
* @constructor
* @param {(NonNegativeInteger|Collection|ArrayBuffer|Iterable)} [arg] - length, typed array, array-like object, buffer, or an iterable
* @param {NonNegativeInteger} [byteOffset=0] - byte offset
* @param {NonNegativeInteger} [length] - view length
* @throws {RangeError} ArrayBuffer byte length must be a multiple of `2`
* @throws {TypeError} if provided only a single argument, must provide a valid argument
* @throws {TypeError} byte offset must be a nonnegative integer
* @throws {RangeError} byte offset must be a multiple of `2`
* @throws {TypeError} view length must be a positive multiple of `2`
* @throws {RangeError} must provide sufficient memory to accommodate byte offset and view length requirements
* @returns {Float16Array} half-precision floating-point number array
*
* @example
* var arr = new Float16Array();
* // returns <Float16Array>
*
* var len = arr.length;
* // returns 0
*
* @example
* var arr = new Float16Array( 5 );
* // returns <Float16Array>
*
* var len = arr.length;
* // returns 5
*
* @example
* var arr = new Float16Array( [ 1.0, 2.0, 3.0, 4.0, 5.0 ] );
* // returns <Float16Array>
*
* var len = arr.length;
* // returns 5
*
* @example
* import ArrayBuffer from '@stdlib/array-buffer';
*
* var buf = new ArrayBuffer( 16 );
* var arr = new Float16Array( buf );
* // returns <Float16Array>
*
* var len = arr.length;
* // returns 8
*
* @example
* import ArrayBuffer from '@stdlib/array-buffer';
*
* var buf = new ArrayBuffer( 16 );
* var arr = new Float16Array( buf, 8 );
* // returns <Float16Array>
*
* var len = arr.length;
* // returns 4
*
* @example
* import ArrayBuffer from '@stdlib/array-buffer';
*
* var buf = new ArrayBuffer( 32 );
* var arr = new Float16Array( buf, 8, 2 );
* // returns <Float16Array>
*
* var len = arr.length;
* // returns 2
*/
function Float16Array() {
	var byteOffset;
	var nargs;
	var buf;
	var tmp;
	var len;
	var arg;
	var i;

	nargs = arguments.length;
	if ( !(this instanceof Float16Array) ) {
		if ( nargs === 0 ) {
			return new Float16Array();
		}
		if ( nargs === 1 ) {
			return new Float16Array( arguments[0] );
		}
		if ( nargs === 2 ) {
			return new Float16Array( arguments[0], arguments[1] );
		}
		return new Float16Array( arguments[0], arguments[1], arguments[2] );
	}
	// Create the underlying data buffer...
	if ( nargs === 0 ) {
		buf = new Uint16Array( 0 ); // backward-compatibility
	} else if ( nargs === 1 ) {
		arg = arguments[ 0 ];
		if ( isNonNegativeInteger( arg ) ) {
			buf = new Uint16Array( arg );
		} else if ( isCollection( arg ) ) {
			if ( isFloat16Array( arg ) ) {
				buf = new Uint16Array( arg.length );
				tmp = new Uint16Array( arg.buffer, arg.byteOffset, arg.length );
				for ( i = 0; i < arg.length; i++ ) {
					buf[ i ] = tmp[ i ];
				}
			} else {
				buf = fromArray( new Uint16Array( arg.length ), arg );
			}
		} else if ( isArrayBuffer( arg ) ) {
			if ( !isInteger( arg.byteLength/BYTES_PER_ELEMENT ) ) {
				throw new RangeError( format( 'nullE9', BYTES_PER_ELEMENT, arg.byteLength ) );
			}
			buf = new Uint16Array( arg );
		} else if ( isObject( arg ) ) {
			if ( HAS_ITERATOR_SYMBOL === false ) {
				throw new TypeError( format( 'null29', arg ) );
			}
			if ( !isFunction( arg[ ITERATOR_SYMBOL ] ) ) {
				throw new TypeError( format( 'null2A', arg ) );
			}
			buf = arg[ ITERATOR_SYMBOL ]();
			if ( !isFunction( buf.next ) ) {
				throw new TypeError( format( 'null2A', arg ) );
			}
			buf = new Uint16Array( fromIterator( buf ) );
		} else {
			throw new TypeError( format( 'null2A', arg ) );
		}
	} else {
		buf = arguments[ 0 ];
		if ( !isArrayBuffer( buf ) ) {
			throw new TypeError( format( 'null2B', buf ) );
		}
		byteOffset = arguments[ 1 ];
		if ( !isNonNegativeInteger( byteOffset ) ) {
			throw new TypeError( format( 'null2C', byteOffset ) );
		}
		if ( !isInteger( byteOffset/BYTES_PER_ELEMENT ) ) {
			throw new RangeError( format( 'nullEA', BYTES_PER_ELEMENT, byteOffset ) );
		}
		if ( nargs === 2 ) {
			len = buf.byteLength - byteOffset;
			if ( !isInteger( len/BYTES_PER_ELEMENT ) ) {
				throw new RangeError( format( 'null2E', BYTES_PER_ELEMENT, len ) );
			}
			buf = new Uint16Array( buf, byteOffset );
		} else {
			len = arguments[ 2 ];
			if ( !isNonNegativeInteger( len ) ) {
				throw new TypeError( format( 'null2F', len ) );
			}
			if ( (len*BYTES_PER_ELEMENT) > (buf.byteLength-byteOffset) ) {
				throw new RangeError( format( 'null2G', len*BYTES_PER_ELEMENT ) );
			}
			buf = new Uint16Array( buf, byteOffset, len );
		}
	}
	setReadOnly( this, '_buffer', buf );
	setReadOnly( this, '_length', buf.length );
	for ( i = 0; i < buf.length; i++ ) {
		setReadWriteAccessor( this, i, getter( this, i ), setter( this, i ) );
	}
	return this;
}

/**
* Size (in bytes) of each array element.
*
* @name BYTES_PER_ELEMENT
* @memberof Float16Array
* @readonly
* @type {PositiveInteger}
* @default 2
*
* @example
* var nbytes = Float16Array.BYTES_PER_ELEMENT;
* // returns 2
*/
setReadOnly( Float16Array, 'BYTES_PER_ELEMENT', BYTES_PER_ELEMENT );

/**
* Constructor name.
*
* @name name
* @memberof Float16Array
* @readonly
* @type {string}
* @default 'Float16Array'
*
* @example
* var str = Float16Array.name;
* // returns 'Float16Array'
*/
setReadOnly( Float16Array, 'name', 'Float16Array' );

/**
* Creates a new 16-bit floating-point number array from an array-like object or an iterable.
*
* @name from
* @memberof Float16Array
* @type {Function}
* @param {(Collection|Iterable)} src - array-like object or iterable
* @param {Function} [clbk] - callback to invoke for each source element
* @param {*} [thisArg] - context
* @throws {TypeError} `this` context must be a constructor
* @throws {TypeError} `this` must be a floating-point array
* @throws {TypeError} first argument must be an array-like object or an iterable
* @throws {TypeError} second argument must be a function
* @returns {Float16Array} half-precision floating-point number array
*
* @example
* var arr = Float16Array.from( [ 1.0, 2.0 ] );
* // returns <Float16Array>[ 1.0, 2.0 ]
*
* @example
* function clbk( v ) {
*     return v * 2.0;
* }
*
* var arr = Float16Array.from( [ 1.0, 2.0 ], clbk );
* // returns <Float16Array>[ 2.0, 4.0 ]
*/
setReadOnly( Float16Array, 'from', function from( src ) {
	var thisArg;
	var nargs;
	var clbk;
	var out;
	var buf;
	var tmp;
	var len;
	var i;
	if ( !isFunction( this ) ) {
		throw new TypeError( format('null01') );
	}
	if ( !isFloatingPointArrayConstructor( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	nargs = arguments.length;
	if ( nargs > 1 ) {
		clbk = arguments[ 1 ];
		if ( !isFunction( clbk ) ) {
			throw new TypeError( format( 'null2H', clbk ) );
		}
		if ( nargs > 2 ) {
			thisArg = arguments[ 2 ];
		}
	}
	if ( isCollection( src ) ) {
		if ( clbk ) {
			len = src.length;
			out = new this( len );
			buf = out._buffer; // eslint-disable-line no-underscore-dangle
			for ( i = 0; i < len; i++ ) {
				buf[ i ] = toWord( f16( clbk.call( thisArg, src[ i ], i ) ) );
			}
			return out;
		}
		return new this( src );
	}
	if ( isObject( src ) && HAS_ITERATOR_SYMBOL && isFunction( src[ ITERATOR_SYMBOL ] ) ) { // eslint-disable-line max-len
		buf = src[ ITERATOR_SYMBOL ]();
		if ( !isFunction( buf.next ) ) {
			throw new TypeError( format( 'null2J', src ) );
		}
		if ( clbk ) {
			tmp = fromIteratorMap( buf, clbk, thisArg );
		} else {
			tmp = fromIterator( buf );
		}
		if ( tmp instanceof Error ) {
			throw tmp;
		}
		len = tmp.length;
		out = new this( len );
		buf = out._buffer; // eslint-disable-line no-underscore-dangle
		for ( i = 0; i < len; i++ ) {
			buf[ i ] = tmp[ i ];
		}
		return out;
	}
	throw new TypeError( format( 'null2J', src ) );
});

/**
* Creates a new 16-bit floating-point number array from a variable number of arguments.
*
* @name of
* @memberof Float16Array
* @type {Function}
* @param {...*} element - array elements
* @throws {TypeError} `this` context must be a constructor
* @throws {TypeError} `this` must be a floating-point number array
* @returns {Float16Array} half-precision floating-point number array
*
* @example
* var arr = Float16Array.of( 1.0, 1.0, 1.0, 1.0 );
* // returns <Float16Array>[ 1.0, 1.0, 1.0, 1.0 ]
*/
setReadOnly( Float16Array, 'of', function of() {
	var args;
	var i;
	if ( !isFunction( this ) ) {
		throw new TypeError( format('null01') );
	}
	if ( !isFloatingPointArrayConstructor( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	args = [];
	for ( i = 0; i < arguments.length; i++ ) {
		args.push( arguments[ i ] );
	}
	return new this( args );
});

/**
* Returns an array element with support for both nonnegative and negative integer indices.
*
* @name at
* @memberof Float16Array.prototype
* @type {Function}
* @param {integer} idx - element index
* @throws {TypeError} `this` must be a floating-point number array
* @throws {TypeError} must provide an integer
* @returns {(number|void)} array element
*
* @example
* var arr = new Float16Array( [ 10.0, 20.0, 30.0 ] );
*
* var v = arr.at( 0 );
* // returns 10.0
*
* v = arr.at( -1 );
* // returns 30.0
*
* v = arr.at( 100 );
* // returns undefined
*/
setReadOnly( Float16Array.prototype, 'at', function at( idx ) {
	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	if ( !isInteger( idx ) ) {
		throw new TypeError( format( 'null8A', idx ) );
	}
	if ( idx < 0 ) {
		idx += this._length;
	}
	if ( idx < 0 || idx >= this._length ) {
		return;
	}
	return fromWord( this._buffer[ idx ] );
});

/**
* Pointer to the underlying data buffer.
*
* @name buffer
* @memberof Float16Array.prototype
* @readonly
* @type {ArrayBuffer}
*
* @example
* var arr = new Float16Array( 10 );
*
* var buf = arr.buffer;
* // returns <ArrayBuffer>
*/
setReadOnlyAccessor( Float16Array.prototype, 'buffer', function get() {
	return this._buffer.buffer;
});

/**
* Size (in bytes) of the array.
*
* @name byteLength
* @memberof Float16Array.prototype
* @readonly
* @type {NonNegativeInteger}
*
* @example
* var arr = new Float16Array( 10 );
*
* var byteLength = arr.byteLength;
* // returns 20
*/
setReadOnlyAccessor( Float16Array.prototype, 'byteLength', function get() {
	return this._buffer.byteLength;
});

/**
* Offset (in bytes) of the array from the start of its underlying `ArrayBuffer`.
*
* @name byteOffset
* @memberof Float16Array.prototype
* @readonly
* @type {NonNegativeInteger}
*
* @example
* var arr = new Float16Array( 10 );
*
* var byteOffset = arr.byteOffset;
* // returns 0
*/
setReadOnlyAccessor( Float16Array.prototype, 'byteOffset', function get() {
	return this._buffer.byteOffset;
});

/**
* Size (in bytes) of each array element.
*
* @name BYTES_PER_ELEMENT
* @memberof Float16Array.prototype
* @readonly
* @type {PositiveInteger}
* @default 2
*
* @example
* var arr = new Float16Array( 10 );
*
* var nbytes = arr.BYTES_PER_ELEMENT;
* // returns 2
*/
setReadOnly( Float16Array.prototype, 'BYTES_PER_ELEMENT', Float16Array.BYTES_PER_ELEMENT );

/**
* Copies a sequence of elements within the array to the position starting at `target`.
*
* @name copyWithin
* @memberof Float16Array.prototype
* @type {Function}
* @param {integer} target - index at which to start copying elements
* @param {integer} start - source index at which to copy elements from
* @param {integer} [end] - source index at which to stop copying elements from
* @throws {TypeError} `this` must be a floating-point array
* @returns {Float16Array} modified array
*
* @example
* var arr = new Float16Array( [ 1.0, 2.0, 3.0, 4.0 ] );
*
* // Copy the first two elements to the last two elements:
* arr.copyWithin( 2, 0, 2 );
* // arr => <Float16Array>[ 1.0, 2.0, 1.0, 2.0 ]
*/
setReadOnly( Float16Array.prototype, 'copyWithin', function copyWithin( target, start ) {
	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	// FIXME: prefer a functional `copyWithin` implementation which addresses lack of universal browser support (e.g., IE11 and Safari) or ensure that typed arrays are polyfilled
	if ( arguments.length === 2 ) {
		this._buffer.copyWithin( target, start );
	} else {
		this._buffer.copyWithin( target, start, arguments[2] );
	}
	return this;
});

/**
* Returns an iterator for iterating over array key-value pairs.
*
* @name entries
* @memberof Float16Array.prototype
* @type {Function}
* @throws {TypeError} `this` must be a floating-point number array
* @returns {Iterator} iterator
*
* @example
* var arr = new Float16Array( [ 1.0, 2.0, 3.0 ] );
*
* var it = arr.entries();
*
* var v = it.next().value;
* // returns [ 0, 1.0 ]
*
* v = it.next().value;
* // returns [ 1, 2.0 ]
*
* v = it.next().value;
* // returns [ 2, 3.0 ]
*
* var bool = it.next().done;
* // returns true
*/
setReadOnly( Float16Array.prototype, 'entries', function entries() {
	var self;
	var iter;
	var len;
	var buf;
	var FLG;
	var i;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	self = this;
	buf = this._buffer;
	len = this._length;

	// Initialize an iteration index:
	i = -1;

	// Create an iterator protocol-compliant object:
	iter = {};
	setReadOnly( iter, 'next', next );
	setReadOnly( iter, 'return', end );

	if ( ITERATOR_SYMBOL ) {
		setReadOnly( iter, ITERATOR_SYMBOL, factory );
	}
	return iter;

	/**
	* Returns an iterator protocol-compliant object containing the next iterated value.
	*
	* @private
	* @returns {Object} iterator protocol-compliant object
	*/
	function next() {
		i += 1;
		if ( FLG || i >= len ) {
			return {
				'done': true
			};
		}
		return {
			'value': [ i, fromWord( buf[ i ] ) ],
			'done': false
		};
	}

	/**
	* Finishes an iterator.
	*
	* @private
	* @param {*} [value] - value to return
	* @returns {Object} iterator protocol-compliant object
	*/
	function end( value ) {
		FLG = true;
		if ( arguments.length ) {
			return {
				'value': value,
				'done': true
			};
		}
		return {
			'done': true
		};
	}

	/**
	* Returns a new iterator.
	*
	* @private
	* @returns {Iterator} iterator
	*/
	function factory() {
		return self.entries();
	}
});

/**
* Tests whether all elements in an array pass a test implemented by a predicate function.
*
* @name every
* @memberof Float16Array.prototype
* @type {Function}
* @param {Function} predicate - predicate function
* @param {*} [thisArg] - predicate function execution context
* @throws {TypeError} `this` must be a floating-point number array
* @throws {TypeError} first argument must be a function
* @returns {boolean} boolean indicating whether all elements pass a test
*
* @example
* function predicate( v ) {
*     return v === 0.0;
* }
*
* var arr = new Float16Array( 3 );
*
* var bool = arr.every( predicate );
* // returns true
*/
setReadOnly( Float16Array.prototype, 'every', function every( predicate, thisArg ) {
	var buf;
	var i;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	if ( !isFunction( predicate ) ) {
		throw new TypeError( format( 'null3c', predicate ) );
	}
	buf = this._buffer;
	for ( i = 0; i < this._length; i++ ) {
		if ( !predicate.call( thisArg, fromWord( buf[ i ] ), i, this ) ) {
			return false;
		}
	}
	return true;
});

/**
* Returns a modified typed array filled with a fill value.
*
* @name fill
* @memberof Float16Array.prototype
* @type {Function}
* @param {number} value - fill value
* @param {integer} [start=0] - starting index (inclusive)
* @param {integer} [end] - ending index (exclusive)
* @throws {TypeError} `this` must be a floating-point number array
* @throws {TypeError} first argument must be a floating-point number
* @throws {TypeError} second argument must be an integer
* @throws {TypeError} third argument must be an integer
* @returns {Float16Array} modified array
*
* @example
* var arr = new Float16Array( 3 );
*
* arr.fill( 1.0, 1 );
* // arr => <Float16Array>[ 0.0, 1.0, 1.0 ]
*/
setReadOnly( Float16Array.prototype, 'fill', function fill( value, start, end ) {
	var buf;
	var len;
	var i;
	var v;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	if ( !isNumber( value ) ) {
		throw new TypeError( format( 'null49', value ) );
	}
	buf = this._buffer;
	len = this._length;
	if ( arguments.length > 1 ) {
		if ( !isInteger( start ) ) {
			throw new TypeError( format( 'null7f', start ) );
		}
		if ( start < 0 ) {
			start += len;
			if ( start < 0 ) {
				start = 0;
			}
		}
		if ( arguments.length > 2 ) {
			if ( !isInteger( end ) ) {
				throw new TypeError( format( 'null2z', end ) );
			}
			if ( end < 0 ) {
				end += len;
				if ( end < 0 ) {
					end = 0;
				}
			}
			if ( end > len ) {
				end = len;
			}
		} else {
			end = len;
		}
	} else {
		start = 0;
		end = len;
	}
	v = toWord( value );
	for ( i = start; i < end; i++ ) {
		buf[ i ] = v;
	}
	return this;
});

/**
* Returns a new array containing the elements of an array which pass a test implemented by a predicate function.
*
* @name filter
* @memberof Float16Array.prototype
* @type {Function}
* @param {Function} predicate - test function
* @param {*} [thisArg] - predicate function execution context
* @throws {TypeError} `this` must be a floating-point number array
* @throws {TypeError} first argument must be a function
* @returns {Float16Array} floating-point number array
*
* @example
* function predicate( v ) {
*     return ( v === 0.0 );
* }
*
* var arr = new Float16Array( [ 0.0, 1.0, 0.0 ] );
*
* var out = arr.filter( predicate );
* // returns <Float16Array>[ 0.0, 0.0 ]
*/
setReadOnly( Float16Array.prototype, 'filter', function filter( predicate, thisArg ) {
	var buf;
	var out;
	var i;
	var v;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	if ( !isFunction( predicate ) ) {
		throw new TypeError( format( 'null3c', predicate ) );
	}
	buf = this._buffer;
	out = [];
	for ( i = 0; i < this._length; i++ ) {
		v = fromWord( buf[ i ] );
		if ( predicate.call( thisArg, v, i, this ) ) {
			out.push( v );
		}
	}
	return new this.constructor( out );
});

/**
* Returns the first element in an array for which a predicate function returns a truthy value.
*
* @name find
* @memberof Float16Array.prototype
* @type {Function}
* @param {Function} predicate - predicate function
* @param {*} [thisArg] - predicate function execution context
* @throws {TypeError} `this` must be a floating-point number array
* @throws {TypeError} first argument must be a function
* @returns {(boolean|void)} array element or undefined
*
* @example
* function predicate( v ) {
*     return v > 0.0;
* }
*
* var arr = new Float16Array( [ 0.0, 1.0, 0.0 ] );
*
* var v = arr.find( predicate );
* // returns 1.0
*/
setReadOnly( Float16Array.prototype, 'find', function find( predicate, thisArg ) {
	var buf;
	var v;
	var i;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	if ( !isFunction( predicate ) ) {
		throw new TypeError( format( 'null3c', predicate ) );
	}
	buf = this._buffer;
	for ( i = 0; i < this._length; i++ ) {
		v = fromWord( buf[ i ] );
		if ( predicate.call( thisArg, v, i, this ) ) {
			return v;
		}
	}
});

/**
* Returns the index of the first element in an array for which a predicate function returns a truthy value.
*
* @name findIndex
* @memberof Float16Array.prototype
* @type {Function}
* @param {Function} predicate - predicate function
* @param {*} [thisArg] - predicate function execution context
* @throws {TypeError} `this` must be a floating-point number array
* @throws {TypeError} first argument must be a function
* @returns {integer} index or -1
*
* @example
* function predicate( v ) {
*     return v > 0.0;
* }
*
* var arr = new Float16Array( [ 0.0, 1.0, 2.0 ] );
*
* var v = arr.findIndex( predicate );
* // returns 1
*/
setReadOnly( Float16Array.prototype, 'findIndex', function findIndex( predicate, thisArg ) {
	var buf;
	var v;
	var i;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	if ( !isFunction( predicate ) ) {
		throw new TypeError( format( 'null3c', predicate ) );
	}
	buf = this._buffer;
	for ( i = 0; i < this._length; i++ ) {
		v = fromWord( buf[ i ] );
		if ( predicate.call( thisArg, v, i, this ) ) {
			return i;
		}
	}
	return -1;
});

/**
* Returns the last element in an array for which a predicate function returns a truthy value.
*
* @name findLast
* @memberof Float16Array.prototype
* @type {Function}
* @param {Function} predicate - predicate function
* @param {*} [thisArg] - predicate function execution context
* @throws {TypeError} `this` must be a floating-point number array
* @throws {TypeError} first argument must be a function
* @returns {(boolean|void)} array element or undefined
*
* @example
* function predicate( v ) {
*     return v > 0.0;
* }
*
* var arr = new Float16Array( [ 0.0, 1.0, 0.0 ] );
*
* var v = arr.findLast( predicate );
* // returns 1.0
*/
setReadOnly( Float16Array.prototype, 'findLast', function findLast( predicate, thisArg ) {
	var buf;
	var v;
	var i;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	if ( !isFunction( predicate ) ) {
		throw new TypeError( format( 'null3c', predicate ) );
	}
	buf = this._buffer;
	for ( i = this._length-1; i >= 0; i-- ) {
		v = fromWord( buf[ i ] );
		if ( predicate.call( thisArg, v, i, this ) ) {
			return v;
		}
	}
});

/**
* Returns the index of the last element in an array for which a predicate function returns a truthy value.
*
* @name findLastIndex
* @memberof Float16Array.prototype
* @type {Function}
* @param {Function} predicate - predicate function
* @param {*} [thisArg] - predicate function execution context
* @throws {TypeError} `this` must be a floating-point number array
* @throws {TypeError} first argument must be a function
* @returns {integer} index or -1
*
* @example
* function predicate( v ) {
*     return v > 0.0;
* }
*
* var arr = new Float16Array( [ 0.0, 1.0, 0.0 ] );
*
* var v = arr.findLastIndex( predicate );
* // returns 1
*/
setReadOnly( Float16Array.prototype, 'findLastIndex', function findLastIndex( predicate, thisArg ) {
	var buf;
	var v;
	var i;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	if ( !isFunction( predicate ) ) {
		throw new TypeError( format( 'null3c', predicate ) );
	}
	buf = this._buffer;
	for ( i = this._length-1; i >= 0; i-- ) {
		v = fromWord( buf[ i ] );
		if ( predicate.call( thisArg, v, i, this ) ) {
			return i;
		}
	}
	return -1;
});

/**
* Invokes a function once for each array element.
*
* @name forEach
* @memberof Float16Array.prototype
* @type {Function}
* @param {Function} fcn - function to invoke
* @param {*} [thisArg] - function invocation context
* @throws {TypeError} `this` must be a floating-point number array
* @throws {TypeError} first argument must be a function
*
* @example
* function log( v, i ) {
*     console.log( '%s: %s', i, v.toString() );
* }
*
* var arr = new Float16Array( [ 0.0, 1.0, 2.0 ] );
*
* arr.forEach( log );
*/
setReadOnly( Float16Array.prototype, 'forEach', function forEach( fcn, thisArg ) {
	var buf;
	var i;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	if ( !isFunction( fcn ) ) {
		throw new TypeError( format( 'null3c', fcn ) );
	}
	buf = this._buffer;
	for ( i = 0; i < this._length; i++ ) {
		fcn.call( thisArg, fromWord( buf[ i ] ), i, this );
	}
});

/**
* Returns an array element.
*
* @private
* @name _get
* @memberof Float16Array.prototype
* @type {Function}
* @param {NonNegativeInteger} idx - element index
* @throws {TypeError} `this` must be a floating-point number array
* @throws {TypeError} must provide a nonnegative integer
* @returns {(number|void)} array element
*/
setReadOnly( Float16Array.prototype, '_get', function get( idx ) {
	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	if ( !isNonNegativeInteger( idx ) ) {
		throw new TypeError( format( 'null2K', idx ) );
	}
	if ( idx >= this._length ) {
		return;
	}
	return fromWord( this._buffer[ idx ] );
});

/**
* Returns a boolean indicating whether an array includes a provided value.
*
* @name includes
* @memberof Float16Array.prototype
* @type {Function}
* @param {number} searchElement - search element
* @param {integer} [fromIndex=0] - starting index (inclusive)
* @throws {TypeError} `this` must be a floating-point number array
* @throws {TypeError} first argument must be a floating-point number
* @throws {TypeError} second argument must be an integer
* @returns {boolean} boolean indicating whether an array includes a value
*
* @example
* var arr = new Float16Array( [ 0.0, 1.0, 2.0, 3.0, 4.0 ] );
*
* var bool = arr.includes( 1.0 );
* // returns true
*
* bool = arr.includes( 1.0, 2 );
* // returns false
*
* bool = arr.includes( 5.0 );
* // returns false
*/
setReadOnly( Float16Array.prototype, 'includes', function includes( searchElement, fromIndex ) {
	var buf;
	var i;
	var v;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	if ( !isNumber( searchElement ) ) {
		throw new TypeError( format( 'null49', searchElement ) );
	}
	if ( arguments.length > 1 ) {
		if ( !isInteger( fromIndex ) ) {
			throw new TypeError( format( 'null7f', fromIndex ) );
		}
		if ( fromIndex < 0 ) {
			fromIndex += this._length;
			if ( fromIndex < 0 ) {
				fromIndex = 0;
			}
		}
	} else {
		fromIndex = 0;
	}
	buf = this._buffer;
	v = toWord( searchElement );
	for ( i = fromIndex; i < this._length; i++ ) {
		if ( v === buf[ i ] ) {
			return true;
		}
	}
	return false;
});

/**
* Returns the first index at which a given element can be found.
*
* @name indexOf
* @memberof Float16Array.prototype
* @type {Function}
* @param {float16} searchElement - element to find
* @param {integer} [fromIndex=0] - starting index (inclusive)
* @throws {TypeError} `this` must be a floating-point number array
* @throws {TypeError} first argument must be a floating-point number
* @throws {TypeError} second argument must be an integer
* @returns {integer} index or -1
*
* @example
* var arr = new Float16Array( [ 0.0, 1.0, 2.0, 3.0, 4.0 ] );
*
* var idx = arr.indexOf( 2.0 );
* // returns 2
*
* idx = arr.indexOf( 2.0, 3 );
* // returns -1
*
* idx = arr.indexOf( 3.0, 3 );
* // returns 3
*/
setReadOnly( Float16Array.prototype, 'indexOf', function indexOf( searchElement, fromIndex ) {
	var buf;
	var i;
	var v;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	if ( !isNumber( searchElement ) ) {
		throw new TypeError( format( 'null49', searchElement ) );
	}
	if ( arguments.length > 1 ) {
		if ( !isInteger( fromIndex ) ) {
			throw new TypeError( format( 'null7f', fromIndex ) );
		}
		if ( fromIndex < 0 ) {
			fromIndex += this._length;
			if ( fromIndex < 0 ) {
				fromIndex = 0;
			}
		}
	} else {
		fromIndex = 0;
	}
	buf = this._buffer;
	v = toWord( searchElement );
	for ( i = fromIndex; i < this._length; i++ ) {
		if ( v === buf[ i ] ) {
			return i;
		}
	}
	return -1;
});

/**
* Returns a new string by concatenating all array elements.
*
* @name join
* @memberof Float16Array.prototype
* @type {Function}
* @param {string} [separator=','] - element separator
* @throws {TypeError} `this` must be a floating-point number array
* @throws {TypeError} first argument must be a string
* @returns {string} string representation
*
* @example
* var arr = new Float16Array( [ 0.0, 1.0, 2.0 ] );
*
* var str = arr.join();
* // returns '0,1,2'
*
* str = arr.join( '|' );
* // returns '0|1|2'
*/
setReadOnly( Float16Array.prototype, 'join', function join( separator ) {
	var buf;
	var out;
	var i;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	if ( arguments.length > 0 ) {
		if ( !isString( separator ) ) {
			throw new TypeError( format( 'null3F', separator ) );
		}
	} else {
		separator = ',';
	}
	buf = this._buffer;
	out = [];
	for ( i = 0; i < this._length; i++ ) {
		out.push( fromWord( buf[ i ] ) );
	}
	return out.join( separator );
});

/**
* Returns an iterator for iterating over each index key in a typed array.
*
* @name keys
* @memberof Float16Array.prototype
* @type {Function}
* @throws {TypeError} `this` must be a floating-point number array
* @returns {Iterator} iterator
*
* @example
* var arr = new Float16Array( [ 1.0, 2.0 ] );
*
* var iter = arr.keys();
*
* var v = iter.next().value;
* // returns 0
*
* v = iter.next().value;
* // returns 1
*
* var bool = iter.next().done;
* // returns true
*/
setReadOnly( Float16Array.prototype, 'keys', function keys() {
	var self;
	var iter;
	var len;
	var FLG;
	var i;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	self = this;
	len = this._length;

	// Initialize an iteration index:
	i = -1;

	// Create an iterator protocol-compliant object:
	iter = {};
	setReadOnly( iter, 'next', next );
	setReadOnly( iter, 'return', end );

	if ( ITERATOR_SYMBOL ) {
		setReadOnly( iter, ITERATOR_SYMBOL, factory );
	}
	return iter;

	/**
	* Returns an iterator protocol-compliant object containing the next iterated value.
	*
	* @private
	* @returns {Object} iterator protocol-compliant object
	*/
	function next() {
		i += 1;
		if ( FLG || i >= len ) {
			return {
				'done': true
			};
		}
		return {
			'value': i,
			'done': false
		};
	}

	/**
	* Finishes an iterator.
	*
	* @private
	* @param {*} [value] - value to return
	* @returns {Object} iterator protocol-compliant object
	*/
	function end( value ) {
		FLG = true;
		if ( arguments.length ) {
			return {
				'value': value,
				'done': true
			};
		}
		return {
			'done': true
		};
	}

	/**
	* Returns a new iterator.
	*
	* @private
	* @returns {Iterator} iterator
	*/
	function factory() {
		return self.keys();
	}
});

/**
* Returns the last index at which a given element can be found.
*
* @name lastIndexOf
* @memberof Float16Array.prototype
* @type {Function}
* @param {number} searchElement - element to find
* @param {integer} [fromIndex] - starting index (inclusive)
* @throws {TypeError} `this` must be a floating-point number
* @throws {TypeError} first argument must be a numeric value
* @throws {TypeError} second argument must be an integer
* @returns {integer} index or -1
*
* @example
* var arr = new Float16Array( [ 0.0, 1.0, 2.0, 3.0, 2.0 ] );
*
* var idx = arr.lastIndexOf( 2.0 );
* // returns 4
*
* idx = arr.lastIndexOf( 2.0, 3 );
* // returns 2
*
* idx = arr.lastIndexOf( 4.0, 3 );
* // returns -1
*
* idx = arr.lastIndexOf( 1.0, -3 );
* // returns 1
*/
setReadOnly( Float16Array.prototype, 'lastIndexOf', function lastIndexOf( searchElement, fromIndex ) {
	var buf;
	var i;
	var v;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	if ( !isNumber( searchElement ) ) {
		throw new TypeError( format( 'null49', searchElement ) );
	}
	if ( arguments.length > 1 ) {
		if ( !isInteger( fromIndex ) ) {
			throw new TypeError( format( 'null7f', fromIndex ) );
		}
		if ( fromIndex >= this._length ) {
			fromIndex = this._length - 1;
		} else if ( fromIndex < 0 ) {
			fromIndex += this._length;
		}
	} else {
		fromIndex = this._length - 1;
	}
	buf = this._buffer;
	v = toWord( searchElement );
	for ( i = fromIndex; i >= 0; i-- ) {
		if ( v === buf[ i ] ) {
			return i;
		}
	}
	return -1;
});

/**
* Number of array elements.
*
* @name length
* @memberof Float16Array.prototype
* @readonly
* @type {NonNegativeInteger}
*
* @example
* var arr = new Float16Array( 10 );
*
* var len = arr.length;
* // returns 10
*/
setReadOnlyAccessor( Float16Array.prototype, 'length', function get() {
	return this._length;
});

/**
* Returns a new array with each element being the result of a provided callback function.
*
* @name map
* @memberof Float16Array.prototype
* @type {Function}
* @param {Function} fcn - callback function
* @param {*} [thisArg] - callback function execution context
* @throws {TypeError} `this` must be a floating-point number array
* @throws {TypeError} first argument must be a function
* @returns {Float16Array} new floating-point number array
*
* @example
* function scale( v ) {
*     return v * 2.0;
* }
*
* var arr = new Float16Array( [ 0.0, 1.0, 2.0 ] );
*
* var out = arr.map( scale );
* // returns <Float16Array>[ 0.0, 2.0, 4.0 ]
*/
setReadOnly( Float16Array.prototype, 'map', function map( fcn, thisArg ) {
	var outbuf;
	var out;
	var buf;
	var i;
	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	if ( !isFunction( fcn ) ) {
		throw new TypeError( format('null3c'), fcn );
	}
	buf = this._buffer;
	out = new this.constructor( this._length );
	outbuf = out._buffer; // eslint-disable-line no-underscore-dangle
	for ( i = 0; i < this._length; i++ ) {
		outbuf[ i ] = toWord( fcn.call( thisArg, fromWord( buf[ i ] ), i, this ) ); // eslint-disable-line max-len
	}
	return out;
});

/**
* Applies a provided callback function to each element of the array, in order, passing in the return value from the calculation on the preceding element and returning the accumulated result upon completion.
*
* @name reduce
* @memberof Float16Array.prototype
* @type {Function}
* @param {Function} reducer - callback function
* @param {*} [initialValue] - initial value
* @throws {TypeError} `this` must be a floating-point number array
* @throws {Error} if not provided an initial value, the array must have at least one element
* @returns {*} accumulated result
*
* @example
* function add( acc, v ) {
*     return acc + v;
* }
*
* var arr = new Float16Array( [ 1.0, 2.0, 3.0 ] );
*
* var out = arr.reduce( add, 0 );
* // returns 6.0
*/
setReadOnly( Float16Array.prototype, 'reduce', function reduce( reducer, initialValue ) {
	var buf;
	var len;
	var acc;
	var i;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	if ( !isFunction( reducer ) ) {
		throw new TypeError( format( 'null3c', reducer ) );
	}
	buf = this._buffer;
	len = this._length;
	if ( arguments.length > 1 ) {
		acc = initialValue;
		i = 0;
	} else {
		if ( len === 0 ) {
			throw new Error( 'invalid operation. If not provided an initial value, an array must contain at least one element.' );
		}
		acc = fromWord( buf[ 0 ] );
		i = 1;
	}
	for ( ; i < len; i++ ) {
		acc = reducer( acc, fromWord( buf[ i ] ), i, this );
	}
	return acc;
});

/**
* Applies a provided callback function to each element of the array, in reverse order, passing in the return value from the calculation on the preceding element and returning the accumulated result upon completion.
*
* @name reduceRight
* @memberof Float16Array.prototype
* @type {Function}
* @param {Function} reducer - callback function
* @param {*} [initialValue] - initial value
* @throws {TypeError} `this` must be a floating-point number array
* @throws {Error} if not provided an initial value, the array must have at least one element
* @returns {*} accumulated result
*
* @example
* function add( acc, v ) {
*     return acc + v;
* }
*
* var arr = new Float16Array( [ 1.0, 2.0, 3.0 ] );
*
* var out = arr.reduceRight( add, 0 );
* // returns 6.0
*/
setReadOnly( Float16Array.prototype, 'reduceRight', function reduceRight( reducer, initialValue ) {
	var buf;
	var len;
	var acc;
	var i;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	if ( !isFunction( reducer ) ) {
		throw new TypeError( format( 'null3c', reducer ) );
	}
	buf = this._buffer;
	len = this._length;
	if ( arguments.length > 1 ) {
		acc = initialValue;
		i = len - 1;
	} else {
		if ( len === 0 ) {
			throw new Error( 'invalid operation. If not provided an initial value, an array must contain at least one element.' );
		}
		acc = fromWord( buf[ len-1 ] );
		i = len - 2;
	}
	for ( ; i >= 0; i-- ) {
		acc = reducer( acc, fromWord( buf[ i ] ), i, this );
	}
	return acc;
});

/**
* Reverses an array in-place.
*
* @name reverse
* @memberof Float16Array.prototype
* @type {Function}
* @throws {TypeError} `this` must be a floating-point number array
* @returns {Float16Array} reversed array
*
* @example
* var arr = new Float16Array( [ 0.0, 1.0, 2.0 ] );
*
* var out = arr.reverse();
* // returns <Float16Array>[ 2.0, 1.0, 0.0 ]
*/
setReadOnly( Float16Array.prototype, 'reverse', function reverse() {
	var buf;
	var tmp;
	var len;
	var N;
	var i;
	var j;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	buf = this._buffer;
	len = this._length;
	N = floor( len / 2 );
	for ( i = 0; i < N; i++ ) {
		j = len - i - 1;
		tmp = buf[ i ];
		buf[ i ] = buf[ j ];
		buf[ j ] = tmp;
	}
	return this;
});

/**
* Sets one or more array elements.
*
* ## Notes
*
* -   When provided a typed array, we must check whether the source array shares the same buffer as the target array and whether the underlying memory overlaps. In particular, we are concerned with the following scenario:
*
*     ```text
*     buf:                ---------------------
*     src: ---------------------
*     ```
*
*     In the above, as we copy values from `src`, we will overwrite values in the `src` view, resulting in duplicated values copied into the end of `buf`, which is not intended. Hence, to avoid overwriting source values, we must **copy** source values to a temporary array.
*
*     In the other overlapping scenario,
*
*     ```text
*     buf: ---------------------
*     src:                ---------------------
*     ```
*
*     by the time we begin copying into the overlapping region, we are copying from the end of `src`, a non-overlapping region, which means we don't run the risk of copying copied values, rather than the original `src` values, as intended.
*
* @name set
* @memberof Float16Array.prototype
* @type {Function}
* @param {(Collection|Float16Array)} value - value(s)
* @param {NonNegativeInteger} [i=0] - element index at which to start writing values
* @throws {TypeError} `this` must be a floating-point number array
* @throws {TypeError} first argument must be a collection
* @throws {TypeError} index argument must be a nonnegative integer
* @throws {RangeError} index argument is out-of-bounds
* @throws {RangeError} target array lacks sufficient storage to accommodate source values
* @returns {void}
*
* @example
* var arr = new Float16Array( 10 );
*
* var v = arr[ 0 ];
* // returns 0.0
*
* arr.set( [ 1.0, 2.0 ], 0 );
*
* v = arr[ 0 ];
* // returns 1.0
*/
setReadOnly( Float16Array.prototype, 'set', function set( value ) {
	var sbuf;
	var idx;
	var buf;
	var tmp;
	var flg;
	var N;
	var i;
	var j;
	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	if ( !isCollection( value ) ) {
		throw new TypeError( format( 'null2O', value ) );
	}
	buf = this._buffer;
	if ( arguments.length > 1 ) {
		idx = arguments[ 1 ];
		if ( !isNonNegativeInteger( idx ) ) {
			throw new TypeError( format( 'null2L', idx ) );
		}
	} else {
		idx = 0;
	}
	N = value.length;
	if ( idx+N > this._length ) {
		throw new RangeError( format('null03') );
	}
	if ( isFloat16Array( value ) ) {
		sbuf = value._buffer; // eslint-disable-line no-underscore-dangle
		flg = true;
	} else {
		sbuf = value;
		flg = false;
	}
	// Check for overlapping memory...
	j = buf.byteOffset + (idx*BYTES_PER_ELEMENT);
	if (
		sbuf.buffer === buf.buffer &&
		(
			sbuf.byteOffset < j &&
			sbuf.byteOffset+sbuf.byteLength > j
		)
	) {
		// We need to copy source values...
		tmp = new Uint16Array( sbuf.length );
		for ( i = 0; i < sbuf.length; i++ ) {
			tmp[ i ] = sbuf[ i ];
		}
		sbuf = tmp;
	}
	if ( flg ) {
		for ( i = 0; i < N; idx++, i++ ) {
			buf[ idx ] = sbuf[ i ];
		}
	} else {
		for ( i = 0; i < N; idx++, i++ ) {
			buf[ idx ] = toWord( sbuf[ i ] );
		}
	}
});

/**
* Copies a portion of a typed array to a new typed array.
*
* @name slice
* @memberof Float16Array.prototype
* @type {Function}
* @param {integer} [begin] - start index (inclusive)
* @param {integer} [end] - end index (exclusive)
* @throws {TypeError} `this` must be a floating-point number array
* @throws {TypeError} first argument must be integer
* @throws {TypeError} second argument must be integer
* @returns {Float16Array} floating-point number array
*
* @example
* var arr = new Float16Array( [ 0.0, 1.0, 2.0, 3.0, 4.0 ] );
*
* var out = arr.slice();
* // returns <Float16Array>[ 0.0, 1.0, 2.0, 3.0, 4.0 ]
*
* out = arr.slice( 1, -2 );
* // returns <Float16Array>[ 1.0, 2.0 ]
*/
setReadOnly( Float16Array.prototype, 'slice', function slice( begin, end ) {
	var outlen;
	var outbuf;
	var out;
	var buf;
	var len;
	var i;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	buf = this._buffer;
	len = this._length;
	if ( arguments.length === 0 ) {
		begin = 0;
		end = len;
	} else {
		if ( !isInteger( begin ) ) {
			throw new TypeError( format( 'null7e', begin ) );
		}
		if ( begin < 0 ) {
			begin += len;
			if ( begin < 0 ) {
				begin = 0;
			}
		}
		if ( arguments.length === 1 ) {
			end = len;
		} else {
			if ( !isInteger( end ) ) {
				throw new TypeError( format( 'null7f', end ) );
			}
			if ( end < 0 ) {
				end += len;
				if ( end < 0 ) {
					end = 0;
				}
			} else if ( end > len ) {
				end = len;
			}
		}
	}
	if ( begin < end ) {
		outlen = end - begin;
	} else {
		outlen = 0;
	}
	out = new this.constructor( outlen );
	outbuf = out._buffer; // eslint-disable-line no-underscore-dangle
	for ( i = 0; i < outlen; i++ ) {
		outbuf[ i ] = buf[ i+begin ];
	}
	return out;
});

/**
* Tests whether at least one element in an array passes a test implemented by a predicate function.
*
* @name some
* @memberof Float16Array.prototype
* @type {Function}
* @param {Function} predicate - predicate function
* @param {*} [thisArg] - predicate function execution context
* @throws {TypeError} `this` must be a floating-point number array
* @throws {TypeError} first argument must be a function
* @returns {boolean} boolean indicating whether at least one element passes a test
*
* @example
* function predicate( v ) {
*     return v > 0.0;
* }
*
* var arr = new Float16Array( [ 0.0, 1.0, 2.0 ] );
*
* var bool = arr.some( predicate );
* // returns true
*/
setReadOnly( Float16Array.prototype, 'some', function some( predicate, thisArg ) {
	var buf;
	var i;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	if ( !isFunction( predicate ) ) {
		throw new TypeError( format( 'null3c', predicate ) );
	}
	buf = this._buffer;
	for ( i = 0; i < this._length; i++ ) {
		if ( predicate.call( thisArg, fromWord( buf[ i ] ), i, this ) ) {
			return true;
		}
	}
	return false;
});

/**
* Sorts an array in-place.
*
* @name sort
* @memberof Float16Array.prototype
* @type {Function}
* @param {Function} [compareFcn] - comparison function
* @throws {TypeError} `this` must be a floating-point number array
* @throws {TypeError} first argument must be a function
* @returns {Float16Array} sorted array
*
* @example
* function compare( a, b ) {
*    if ( a < b ) {
*        return -1;
*    }
*    if ( a > b ) {
*        return 1;
*    }
*    return 0;
* }
*
* var arr = new Float16Array( [ 1.0, 0.0, 2.0 ] );
*
* arr.sort( compare );
* // arr => <Float16Array>[ 0.0, 1.0, 2.0 ]
*/
setReadOnly( Float16Array.prototype, 'sort', function sort( compareFcn ) {
	var buf;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	buf = this._buffer;
	if ( arguments.length === 0 ) {
		buf.sort( defaultCompare );
		return this;
	}
	if ( !isFunction( compareFcn ) ) {
		throw new TypeError( format( 'null3c', compareFcn ) );
	}
	buf.sort( compare );
	return this;

	/**
	* Default comparison function for float16 values.
	*
	* @private
	* @param {unsigned16} a - unsigned 16-bit integer for comparison
	* @param {unsigned16} b - unsigned 16-bit integer for comparison
	* @returns {number} comparison result
	*/
	function defaultCompare( a, b ) { // eslint-disable-line stdlib/no-unnecessary-nested-functions
		var x = fromWord( a );
		var y = fromWord( b );

		// Handle NaN...
		if ( isnan( x ) && isnan( y ) ) {
			return 0;
		}
		if ( isnan( x ) ) {
			return 1;
		}
		if ( isnan( y ) ) {
			return -1;
		}
		// Normal comparison
		if ( x < y ) {
			return -1;
		}
		if ( x > y ) {
			return 1;
		}
		return 0;
	}

	/**
	* Comparison function wrapper.
	*
	* @private
	* @param {unsigned16} a - unsigned 16-bit integer for comparison
	* @param {unsigned16} b - unsigned 16-bit integer for comparison
	* @returns {number} comparison result
	*/
	function compare( a, b ) {
		return compareFcn( fromWord( a ), fromWord( b ) );
	}
});

/**
* Creates a new typed array view over the same underlying `ArrayBuffer` and with the same underlying data type as the host array.
*
* @name subarray
* @memberof Float16Array.prototype
* @type {Function}
* @param {integer} [begin] - start index (inclusive)
* @param {integer} [end] - end index (exclusive)
* @throws {TypeError} `this` must be a floating-point number array
* @throws {TypeError} first argument must be an integer
* @throws {TypeError} second argument must be an integer
* @returns {Float16Array} subarray
*
* @example
* var arr = new Float16Array( [ 0.0, 1.0, 2.0, 3.0, 4.0 ] );
*
* var subarr = arr.subarray();
* // returns <Float16Array>[ 0.0, 1.0, 2.0, 3.0, 4.0 ]
*
* subarr = arr.subarray( 1, -2 );
* // returns <Float16Array>[ 1.0, 2.0 ]
*/
setReadOnly( Float16Array.prototype, 'subarray', function subarray( begin, end ) {
	var offset;
	var buf;
	var len;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	buf = this._buffer;
	len = this._length;
	if ( arguments.length === 0 ) {
		begin = 0;
		end = len;
	} else {
		if ( !isInteger( begin ) ) {
			throw new TypeError( format( 'null7e', begin ) );
		}
		if ( begin < 0 ) {
			begin += len;
			if ( begin < 0 ) {
				begin = 0;
			}
		}
		if ( arguments.length === 1 ) {
			end = len;
		} else {
			if ( !isInteger( end ) ) {
				throw new TypeError( format( 'null7f', end ) );
			}
			if ( end < 0 ) {
				end += len;
				if ( end < 0 ) {
					end = 0;
				}
			} else if ( end > len ) {
				end = len;
			}
		}
	}
	if ( begin >= len ) {
		len = 0;
		offset = buf.byteLength;
	} else if ( begin >= end ) {
		len = 0;
		offset = buf.byteOffset + ( begin*BYTES_PER_ELEMENT );
	} else {
		len = end - begin;
		offset = buf.byteOffset + ( begin*BYTES_PER_ELEMENT );
	}
	return new this.constructor( buf.buffer, offset, ( len < 0 ) ? 0 : len );
});

/**
* Serializes an array as a locale-specific string.
*
* @name toLocaleString
* @memberof Float16Array.prototype
* @type {Function}
* @param {(string|Array<string>)} [locales] - locale identifier(s)
* @param {Object} [options] - configuration options
* @throws {TypeError} `this` must be a floating-point number array
* @throws {TypeError} first argument must be a string or an array of strings
* @throws {TypeError} options argument must be an object
* @returns {string} string representation
*
* @example
* var arr = new Float16Array( [ 0.0, 1.0, 2.0 ] );
*
* var str = arr.toLocaleString();
* // returns '0,1,2'
*/
setReadOnly( Float16Array.prototype, 'toLocaleString', function toLocaleString( locales, options ) {
	var opts;
	var loc;
	var out;
	var buf;
	var i;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	if ( arguments.length === 0 ) {
		loc = [];
	} else if ( isString( locales ) || isStringArray( locales ) ) {
		loc = locales;
	} else {
		throw new TypeError( format( 'invalid argument. First argument must be a string or an array of strings. Value: `%s`.', locales ) );
	}
	if ( arguments.length < 2 ) {
		opts = {};
	} else if ( isObject( options ) ) {
		opts = options;
	} else {
		throw new TypeError( format( 'null2V', options ) );
	}
	buf = this._buffer;
	out = [];
	for ( i = 0; i < this._length; i++ ) {
		out.push( fromWord( buf[ i ] ) );
	}
	return out.toLocaleString( loc, opts );
});

/**
* Returns a new typed array containing the elements in reversed order.
*
* @name toReversed
* @memberof Float16Array.prototype
* @type {Function}
* @throws {TypeError} `this` must be a floating-point number array
* @returns {Float16Array} reversed array
*
* @example
* var arr = new Float16Array( [ 0.0, 1.0, 2.0 ] );
*
* var out = arr.toReversed();
* // returns <Float16Array>[ 2.0, 1.0, 0.0 ]
*
* var bool = ( out === arr );
* // returns false
*/
setReadOnly( Float16Array.prototype, 'toReversed', function toReversed() {
	var outbuf;
	var out;
	var len;
	var buf;
	var i;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	len = this._length;
	out = new this.constructor( len );
	buf = this._buffer;
	outbuf = out._buffer; // eslint-disable-line no-underscore-dangle
	for ( i = 0; i < len; i++ ) {
		outbuf[ i ] = buf[ len - i - 1 ];
	}
	return out;
});

/**
* Returns a new typed array containing the elements in sorted order.
*
* @name toSorted
* @memberof Float16Array.prototype
* @type {Function}
* @param {Function} [compareFcn] - comparison function
* @throws {TypeError} `this` must be a floating-point number array
* @throws {TypeError} first argument must be a function
* @returns {Float16Array} sorted array
*
* @example
* function compare( a, b ) {
*    if ( a < b ) {
*        return -1;
*    }
*    if ( a > b ) {
*        return 1;
*    }
*    return 0;
* }
*
* var arr = new Float16Array( [ 2.0, 0.0, 1.0 ] );
*
* var out = arr.toSorted( compare );
* // returns <Float16Array>[ 0.0, 1.0, 2.0 ]
*
* var bool = ( out === arr );
* // returns false
*/
setReadOnly( Float16Array.prototype, 'toSorted', function toSorted( compareFcn ) {
	var out;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	out = new this.constructor( this );
	if ( arguments.length === 0 ) {
		return out.sort();
	}
	return out.sort( compareFcn );
});

/**
* Serializes an array as a string.
*
* @name toString
* @memberof Float16Array.prototype
* @type {Function}
* @throws {TypeError} `this` must be a floating-point number array
* @returns {string} string representation
*
* @example
* var arr = new Float16Array( [ 0.0, 1.0, 2.0 ] );
*
* var str = arr.toString();
* // returns '0,1,2'
*/
setReadOnly( Float16Array.prototype, 'toString', function toString() {
	var out;
	var buf;
	var i;
	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	buf = this._buffer;
	out = [];
	for ( i = 0; i < this._length; i++ ) {
		out.push( fromWord( buf[ i ] ) );
	}
	return out.join( ',' );
});

/**
* Returns an iterator for iterating over each value in a typed array.
*
* @name values
* @memberof Float16Array.prototype
* @type {Function}
* @throws {TypeError} `this` must be a floating-point number array
* @returns {Iterator} iterator
*
* @example
* var arr = new Float16Array( [ 0.0, 1.0 ] );
*
* var iter = arr.values();
*
* var v = iter.next().value;
* // returns 0.0
*
* v = iter.next().value;
* // returns 1.0
*
* var bool = iter.next().done;
* // returns true
*/
setReadOnly( Float16Array.prototype, 'values', function values() {
	var iter;
	var self;
	var len;
	var FLG;
	var buf;
	var i;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	self = this;
	buf = this._buffer;
	len = this._length;

	// Initialize an iteration index:
	i = -1;

	// Create an iterator protocol-compliant object:
	iter = {};
	setReadOnly( iter, 'next', next );
	setReadOnly( iter, 'return', end );

	if ( ITERATOR_SYMBOL ) {
		setReadOnly( iter, ITERATOR_SYMBOL, factory );
	}
	return iter;

	/**
	* Returns an iterator protocol-compliant object containing the next iterated value.
	*
	* @private
	* @returns {Object} iterator protocol-compliant object
	*/
	function next() {
		i += 1;
		if ( FLG || i >= len ) {
			return {
				'done': true
			};
		}
		return {
			'value': fromWord( buf[ i ] ),
			'done': false
		};
	}

	/**
	* Finishes an iterator.
	*
	* @private
	* @param {*} [value] - value to return
	* @returns {Object} iterator protocol-compliant object
	*/
	function end( value ) {
		FLG = true;
		if ( arguments.length ) {
			return {
				'value': value,
				'done': true
			};
		}
		return {
			'done': true
		};
	}

	/**
	* Returns a new iterator.
	*
	* @private
	* @returns {Iterator} iterator
	*/
	function factory() {
		return self.values();
	}
});

/**
* Returns a new typed array with the element at a provided index replaced with a provided value.
*
* @name with
* @memberof Float16Array.prototype
* @type {Function}
* @param {integer} index - element index
* @param {number} value - new value
* @throws {TypeError} `this` must be a floating-point number array
* @throws {TypeError} first argument must be an integer
* @throws {RangeError} index argument is out-of-bounds
* @throws {TypeError} second argument must be a floating-point number
* @returns {Float16Array} new typed array
*
* @example
* var arr = new Float16Array( [ 0.0, 1.0, 2.0 ] );
*
* var out = arr.with( 0, 3.0 );
* // returns <Float16Array>[ 3.0, 1.0, 2.0 ]
*
* var bool = ( out === arr );
* // returns false
*/
setReadOnly( Float16Array.prototype, 'with', function copyWith( index, value ) {
	var out;
	var len;

	if ( !isFloat16Array( this ) ) {
		throw new TypeError( 'invalid invocation. `this` is not a floating-point number array.' );
	}
	if ( !isInteger( index ) ) {
		throw new TypeError( format( 'null7e', index ) );
	}
	len = this._length;
	if ( index < 0 ) {
		index += len;
	}
	if ( index < 0 || index >= len ) {
		throw new RangeError( format( 'nullFP', index ) );
	}
	if ( !isNumber( value ) ) {
		throw new TypeError( format( 'null4A', value ) );
	}
	out = new this.constructor( this );
	out[ index ] = value;
	return out;
});


// EXPORTS //

export default Float16Array;
